import { useAuth } from '@/contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireRole?: 'owner' | 'admin' | 'engineer' | 'viewer'
  requirePlatformAdmin?: boolean
}

const ROLE_HIERARCHY: Record<string, number> = {
  viewer: 0,
  engineer: 1,
  admin: 2,
  owner: 3,
}

export default function ProtectedRoute({ children, requireRole, requirePlatformAdmin }: ProtectedRouteProps) {
  const { user, loading } = useAuth()
  const [adminChecked, setAdminChecked] = useState(!requirePlatformAdmin)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    if (!requirePlatformAdmin) {
      setAdminChecked(true)
      return
    }
    if (!user) {
      setAdminChecked(true)
      return
    }

    let cancelled = false
    setAdminChecked(false)

    async function check() {
      try {
        const { data } = await supabase
          .from('platform_admins')
          .select('id')
          .eq('user_id', user!.id)
          .single()
        if (!cancelled) setIsAdmin(!!data)
      } catch (err) {
        console.error('ProtectedRoute: admin check failed', err)
        if (!cancelled) setIsAdmin(false)
      } finally {
        if (!cancelled) setAdminChecked(true)
      }
    }

    check()
    return () => { cancelled = true }
  }, [requirePlatformAdmin, user])

  if (loading || !adminChecked) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }} />
      </div>
    )
  }

  if (!user) return <Navigate to="/login" replace />

  if (requirePlatformAdmin && !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (requireRole) {
    const userLevel = ROLE_HIERARCHY[user.company_role || 'viewer'] ?? 0
    const requiredLevel = ROLE_HIERARCHY[requireRole] ?? 0
    if (userLevel < requiredLevel) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}
